import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage, setGlobalLogoutHandler } from '@/api/api';
import { useNavigate } from 'react-router-dom';
import type { UseAuthReturn } from '@/hooks/types';
import type { User, RegisterRequest, EmailVerificationPendingResponse } from '@/types';
import type { AppleUserData } from '@/types/apple';
import { analytics } from '@/utils/analytics';
import { useUiStore } from '@/store/uiStore';
import { useConversationStore } from '@/store/conversationStore';
import { useAudioStore } from '@/store/audioStore';
import { resetChatModuleState } from '@/hooks/useChat';

/** Structured error thrown when email verification is required. */
export interface EmailVerificationError {
  code: 'email_verification_required' | 'email_not_verified';
  email: string;
  message: string;
}

function isVerificationPending(resp: unknown): resp is EmailVerificationPendingResponse {
  return (
    typeof resp === 'object' &&
    resp !== null &&
    'requires_email_verification' in resp &&
    (resp as Record<string, unknown>)['requires_email_verification'] === true
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- utility type guard, not a component
export function isEmailVerificationError(err: unknown): err is EmailVerificationError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    ((err as Record<string, unknown>)['code'] === 'email_verification_required' ||
      (err as Record<string, unknown>)['code'] === 'email_not_verified')
  );
}

// Context definition
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = React.createContext<UseAuthReturn | undefined>(undefined);

/**
 * AuthProvider Component
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // State
  const [token, setToken] = React.useState<string | null>(() => localStorage.getItem('auth_token'));
  const [user, setUser] = React.useState<User | null>(null);
  const [isInitializing, setIsInitializing] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openModal = useUiStore((state) => state.openModal);
  const openAlert = useUiStore((state) => state.openAlert);

  // --- Actions ---

  const logout = React.useCallback(() => {
    resetChatModuleState();
    useConversationStore.getState().reset();
    useAudioStore.getState().stop();
    queryClient.clear();
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);

  React.useEffect(() => {
    setGlobalLogoutHandler(logout);
  }, [logout]);

  /**
   * Internal helper to fetch user profile AND credit balance.
   * Merges lifebar_max into user object to ensure UI updates correctly.
   */
  const fetchUserProfile = React.useCallback(async () => {
    try {
      // Fetch profile and credit balance in parallel
      // We catch credit errors silently to avoid blocking the app if the credit service is down
      const [profile, creditData] = await Promise.all([
        api.users.getMe(),
        api.credits.getBalance().catch(() => null),
      ]);

      const fullProfile = { ...profile };

      // If credit data is available, override/augment the user profile
      if (creditData) {
        fullProfile.credits = creditData.balance;
        fullProfile.lifebar_max = creditData.lifebar_max;
      }

      setUser(fullProfile);
    } catch {
      // Profile fetch is critical — without it the user sees "Guest" state.
      // Force logout so they land on login instead of a broken shell.
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    }
  }, []);

  // Load User Profile on Mount
  React.useEffect(() => {
    const initAuth = async () => {
      setIsInitializing(true);
      if (token && !user) {
        await fetchUserProfile();
      }
      setIsInitializing(false);
    };
    initAuth();
  }, [token, user, fetchUserProfile]);

  const refreshProfile = React.useCallback(async () => {
    if (!token) return;
    await fetchUserProfile();
  }, [token, fetchUserProfile]);

  const login = React.useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await api.auth.login(email, password);
        localStorage.setItem('auth_token', resp.access_token);
        setToken(resp.access_token);

        // Initial user set from login response (might lack lifebar_max)
        setUser(resp.user);
        await fetchUserProfile();

        analytics.login('email');
        // First-ever login: fire conversion events + welcome modal
        if (resp.is_new_user) {
          analytics.completeRegistration();
          analytics.accountCreated(resp.user.id);
          openModal('welcome');
        }
      } catch (err) {
        const errObj = err as Record<string, unknown> | undefined;
        if (errObj && errObj['status'] === 403) {
          const detail = errObj['detail'];
          // Email not verified (detail is structured object with code)
          if (typeof detail === 'object' && detail !== null) {
            const detailObj = detail as Record<string, unknown>;
            if (detailObj['code'] === 'email_not_verified') {
              const verificationError: EmailVerificationError = {
                code: 'email_not_verified',
                email: (detailObj['email'] as string) || email,
                message:
                  (detailObj['message'] as string) || 'Please verify your email before logging in.',
              };
              throw verificationError;
            }
          }
          // Ban/deactivation (detail is plain string reason)
          if (typeof detail === 'string') {
            openAlert({
              title: 'Account Suspended',
              message: detail,
              primaryLabel: 'Got it',
            });
          }
        }
        const msg = getErrorMessage(err);
        setError(msg);
        analytics.loginFailed('email', msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUserProfile, openAlert, openModal]
  );

  const register = React.useCallback(
    async (data: RegisterRequest): Promise<EmailVerificationPendingResponse | void> => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await api.auth.register(data);

        if (isVerificationPending(resp)) {
          return resp;
        }

        localStorage.setItem('auth_token', resp.access_token);
        setToken(resp.access_token);

        setUser(resp.user);
        await fetchUserProfile(); // Fetch lifebar details

        analytics.signUp('email');
      } catch (err) {
        if (isEmailVerificationError(err)) {
          throw err;
        }
        const msg = getErrorMessage(err);
        setError(msg);
        analytics.signUpFailed('email', msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUserProfile]
  );

  const googleLogin = React.useCallback(
    async (accessToken: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await api.auth.googleLogin(accessToken);
        localStorage.setItem('auth_token', resp.access_token);
        setToken(resp.access_token);

        setUser(resp.user);
        await fetchUserProfile(); // Fetch lifebar details

        analytics.login('google');
        // First-ever login: fire conversion events + welcome modal
        if (resp.is_new_user) {
          analytics.completeRegistration();
          analytics.accountCreated(resp.user.id);
          openModal('welcome');
        }
      } catch (err) {
        const errObj = err as Record<string, unknown> | undefined;
        if (errObj && errObj['status'] === 403 && typeof errObj['detail'] === 'string') {
          openAlert({
            title: 'Account Suspended',
            message: errObj['detail'] as string,
            primaryLabel: 'Got it',
          });
        }
        const msg = getErrorMessage(err);
        setError(msg);
        analytics.loginFailed('google', msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUserProfile, openAlert, openModal]
  );

  const appleLogin = React.useCallback(
    async (idToken: string, userData?: AppleUserData) => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await api.auth.appleLogin(idToken, userData);
        localStorage.setItem('auth_token', resp.access_token);
        setToken(resp.access_token);

        setUser(resp.user);
        await fetchUserProfile();

        analytics.login('apple');
        // First-ever login: fire conversion events + welcome modal
        if (resp.is_new_user) {
          analytics.completeRegistration();
          analytics.accountCreated(resp.user.id);
          openModal('welcome');
        }
      } catch (err) {
        const errObj = err as Record<string, unknown> | undefined;
        if (errObj && errObj['status'] === 403 && typeof errObj['detail'] === 'string') {
          openAlert({
            title: 'Account Suspended',
            message: errObj['detail'] as string,
            primaryLabel: 'Got it',
          });
        }
        const msg = getErrorMessage(err);
        setError(msg);
        analytics.loginFailed('apple', msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUserProfile, openAlert, openModal]
  );

  const updateUser = React.useCallback((updatedUser: User) => {
    // Preserve lifebar_max if the update doesn't include it
    setUser((prev) => (prev ? { ...prev, ...updatedUser } : updatedUser));
    analytics.profileUpdated(['user_updated']);
  }, []);

  const deleteAccount = React.useCallback(
    async (currentPassword?: string) => {
      setIsLoading(true);
      setError(null);
      analytics.accountDeletionRequested();
      try {
        await api.users.deleteAccount(currentPassword);
        logout();
      } catch (err) {
        const msg = getErrorMessage(err);
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [logout]
  );

  const cancelAccountDeletion = React.useCallback(async () => {
    await api.users.cancelDeletion();
    await refreshProfile();
  }, [refreshProfile]);

  // Show deletion warning when account deletion is scheduled
  React.useEffect(() => {
    if (!user?.deletion_requested_at) return;
    const requested = new Date(user.deletion_requested_at);
    const deletionDate = new Date(requested.getTime() + 30 * 24 * 60 * 60 * 1000);
    const days = Math.max(
      0,
      Math.ceil((deletionDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    );
    const formatted = deletionDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    openAlert({
      title: 'Account Deletion Scheduled',
      message: `Your account will be permanently deleted on ${formatted} (${days} days remaining).`,
      primaryLabel: 'Got it',
      secondaryLabel: 'Stop deletion',
      secondaryAction: cancelAccountDeletion,
    });
  }, [user?.deletion_requested_at, openAlert, cancelAccountDeletion]);

  const value: UseAuthReturn = React.useMemo(
    () => ({
      token,
      user,
      isAuthenticated: !!token,
      isInitializing,
      isLoading,
      error,
      login,
      register,
      googleLogin,
      appleLogin,
      logout,
      updateUser,
      deleteAccount,
      refreshProfile,
    }),
    [
      token,
      user,
      isInitializing,
      isLoading,
      error,
      login,
      register,
      googleLogin,
      appleLogin,
      logout,
      updateUser,
      deleteAccount,
      refreshProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
